import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose, MdFileDownload, MdCalendarToday } from 'react-icons/md';
import { FaFileExcel } from 'react-icons/fa';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useLanguage } from '../context/LanguageContext';

export default function ExportDataModal({ companyId, companyName, onClose }) {
    const { t } = useLanguage();
    const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [loading, setLoading] = useState(false);

    const handleExport = async (e) => {
        e.preventDefault();
        
        if (!startDate || !endDate) {
            return toast.error(t('deleteDataModal.selectDateRange'));
        }

        try {
            setLoading(true);
            const url = `/sensor-data/export/excel?companyId=${companyId}&startDate=${startDate}&endDate=${endDate}`;
            
            // Fetch the file as blob
            const response = await api.get(url, { responseType: 'blob' });
            
            // Create a temporary link to trigger download
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', `nortech-sensor-data-${startDate}-to-${endDate}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
            
            toast.success(t('sensorDetail.excelDownloaded'));
            onClose();
        } catch (err) {
            console.error('Export error:', err);
            toast.error(t('sensorDetail.excelFailed'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AnimatePresence>
            <div className="modal-overlay" onClick={onClose}>
                <motion.div
                    className="modal"
                    onClick={e => e.stopPropagation()}
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 20 }}
                    style={{ maxWidth: 450 }}
                >
                    <div className="modal-header">
                        <div className="modal-title">
                            <div className="modal-title-icon" style={{ background: 'rgba(16, 124, 16, 0.15)', color: '#107c10' }}>
                                <FaFileExcel />
                            </div>
                            {t('exportModal.title')}
                        </div>
                        <button className="modal-close" onClick={onClose}><MdClose /></button>
                    </div>

                    <form onSubmit={handleExport} className="modal-body">
                        <p 
                            style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, lineHeight: 1.5 }}
                            dangerouslySetInnerHTML={{ __html: t('exportModal.desc', { companyName }) }}
                        />

                        <div className="form-group">
                            <label className="form-label">
                                <MdCalendarToday style={{ marginRight: 6, opacity: 0.7 }} />
                                {t('exportModal.startDate')}
                            </label>
                            <input
                                type="date"
                                className="form-input"
                                value={startDate}
                                onChange={e => setStartDate(e.target.value)}
                                required
                            />
                        </div>

                        <div className="form-group" style={{ marginTop: 15 }}>
                            <label className="form-label">
                                <MdCalendarToday style={{ marginRight: 6, opacity: 0.7 }} />
                                {t('exportModal.endDate')}
                            </label>
                            <input
                                type="date"
                                className="form-input"
                                value={endDate}
                                onChange={e => setEndDate(e.target.value)}
                                required
                            />
                        </div>

                        <div className="modal-footer" style={{ marginTop: 24, padding: 0, border: 'none' }}>
                            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                                {t('common.cancel')}
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={loading} style={{ flex: 1, background: '#107c10', borderColor: '#107c10' }}>
                                {loading ? <span className="spinner spinner-sm" /> : <MdFileDownload />}
                                {t('sensorDetail.exportExcel')}
                            </button>
                        </div>
                    </form>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
